import React, { useEffect, useState } from 'react';
import axios from 'axios';

import ckLogo from '../../lib/images/ck-nav-logo-2022.svg';

const coBrandedLogo = (publicKey) => {
  return `https://creditkey-assets.s3-us-west-2.amazonaws.com/co_brands/${publicKey}-ck-logo.svg`;
};

// Module-level cache to persist across component unmounts/remounts
const fetchedPublicKeys = new Set();
const logoCache = new Map(); // Cache of merchant public key -> logo URL

// Test helper to reset the cache
export const resetLogoCache = () => {
  fetchedPublicKeys.clear();
  logoCache.clear();
};

export default function Logo({ merchant }) {
  const merchantPublicKey = merchant?.public_key;
  // Initialize with cached logo if available, otherwise use default
  const [logo, setLogo] = useState(() => {
    if (merchantPublicKey && logoCache.has(merchantPublicKey)) {
      return logoCache.get(merchantPublicKey);
    }
    return ckLogo;
  });

  useEffect(() => {
    let isMounted = true;

    // Fail fast if no merchant
    if (!merchantPublicKey) {
      setLogo(ckLogo); // Reset to default when merchant is cleared
      return;
    }

    // If we've already fetched this merchant, use the cached logo
    if (fetchedPublicKeys.has(merchantPublicKey)) {
      const cachedLogo = logoCache.get(merchantPublicKey);
      if (cachedLogo) {
        setLogo(cachedLogo);
      }
      return;
    }

    const logoUrl = coBrandedLogo(merchantPublicKey);

    axios
      .create()
      .head(logoUrl)
      .then(() => {
        // Record that we've fetched for this public key
        fetchedPublicKeys.add(merchantPublicKey);
        logoCache.set(merchantPublicKey, logoUrl);
        if (isMounted) {
          setLogo(logoUrl);
        }
      })
      .catch(() => {
        // Record that we've fetched for this public key
        fetchedPublicKeys.add(merchantPublicKey);
        logoCache.set(merchantPublicKey, ckLogo);
        if (isMounted) {
          setLogo(ckLogo);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [merchantPublicKey]);

  return <img alt="CreditKey Logo" src={logo} className="w-3/5 sm:w-3/4" />;
}
